import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getQcReportHtml } from '@services/orderDashboard';

// Standalone route (outside DefaultLayout, see AppRoutes.js) so a QC report can be opened in its
// own tab with a clean, report-id URL. It fetches the report the normal authenticated way
// (Authorization header via ApiClient) instead of putting the auth token in the URL, then renders
// the fetched document — a full standalone HTML page in its own right — inside an iframe so its
// styles never mix with the admin app's.
// Being outside the guarded routes, it sends signed-out users to the login page itself, leaving
// its path behind so Login brings them back to the report.
const QcReportViewer = () => {
	const { reportId } = useParams();
	const navigate = useNavigate();
	const [html, setHtml] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const redirectToLogin = () => {
			sessionStorage.setItem('postLoginRedirect', window.location.pathname);
			navigate('/login', { replace: true });
		};

		if (!localStorage.getItem('token')) {
			redirectToLogin();
			return;
		}

		getQcReportHtml(reportId)
			.then((data) => {
				if (!cancelled) setHtml(data);
			})
			.catch((err) => {
				if (cancelled) return;
				// An expired token: the shared 401 handler can't read this text response, so
				// clear the stale session here and ask the user to sign in again.
				if (err?.response?.status === 401) {
					localStorage.removeItem('token');
					redirectToLogin();
					return;
				}
				setError('Unable to load this inspection report.');
			});
		return () => {
			cancelled = true;
		};
	}, [reportId, navigate]);

	useEffect(() => {
		const match = html && html.match(/<title>([^<]*)<\/title>/i);
		if (match) document.title = match[1];
	}, [html]);

	if (error) {
		return <p style={{ padding: 24, fontFamily: 'sans-serif' }}>{error}</p>;
	}

	if (!html) {
		return null;
	}

	return (
		<iframe
			title={`QC Report ${reportId}`}
			srcDoc={html}
			style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 'none' }}
		/>
	);
};

export default QcReportViewer;
